import LocationOnIcon from '@mui/icons-material/LocationOn';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { Backdrop, CircularProgress, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import Grid from '@mui/material/Grid';
import { createMaritimeOrderInfoData, MaritimeOrderInfo, Port } from 'adoms-common-lib';
import dayjs from 'dayjs';
import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { CustomerURLInformationUtil } from '../../common/customerURLInformationUtil';
import { MEDICAL_STUB_SIMPLE_SCHEDULE, searchSimpleSchedule } from '../../common/schedulePickerUtil';
import { OrderBackNextButtons } from '../../components/atoms/OrderBackNextButtons';
import ApplicationTooBar from '../../components/molecule/ApplicationToolBar';
import OrderStepBar, { Process } from '../../components/molecule/OrderStepBar';
import DeliveryTimePicker from '../../components/organisms/DeliveryTimePicker';
import { APIConnector } from '../../connector/APIConnector';
import { SectionGrid } from '../../components/organisms/SectionGrid';

const useStyles = makeStyles()((theme) => ({
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  paper: {
    display: 'flex',
    padding: '15px',
    textAlign: 'center',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    verticalAlign: 'middle',
  },
  select: {
    width: 200,
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
}));


/**
 * フライト希望日時・配送区間指定画面
 */
export default function MaritimeOrderFlightSearchInput() {
  const navigate = useNavigate();
  const location = useLocation();
  const customerURLInformation = CustomerURLInformationUtil.instance.getCustomerURLInformation();
  let orderForEdit = location.state?.orderForEdit as MaritimeOrderInfo;
  const [order, setOrder] = React.useState<MaritimeOrderInfo>(orderForEdit ? orderForEdit : createMaritimeOrderInfoData());
  const { classes } = useStyles();
  const [partner, setPartner] = React.useState(orderForEdit?.businessPartnerId ? orderForEdit.businessPartnerId : '');
  const [departure, setDeparture] = React.useState(orderForEdit?.departure?.id ? orderForEdit.departure.id : '');
  const [arrival, setArrival] = React.useState(orderForEdit?.arrival?.id ? orderForEdit.arrival.id : '');
  const [departurePortList, setDeparturePortList] = React.useState([] as Port[]);
  const [allPortList, setAllPortList] = React.useState([] as Port[]);
  const [arrivalPortList, setArrivalPortList] = React.useState([] as Port[]);
  const [isDisplayLoadingMark, setDisplayLoadingMark] = React.useState<boolean>(false);
  const [isNavigatingNext, setIsNavigatingNext] = React.useState(false);
  const [schedulePickerState, setSchedulePickerState] = React.useState(
    {
      mode: 'ASAP' as 'ASAP' | 'SCHEDULED',
      date: MEDICAL_STUB_SIMPLE_SCHEDULE[0]?.date,
      slotIndex: MEDICAL_STUB_SIMPLE_SCHEDULE[0]?.slots.findIndex(s => s.enabled)
    }
  );

  const isNextDisabled = useMemo(() => {
    if (isNavigatingNext) return true;
    if (partner === '' || departure === '' || arrival === '') return true;

    if (schedulePickerState.mode === 'SCHEDULED') {
      const selectedDay = schedulePickerState.date
        ? MEDICAL_STUB_SIMPLE_SCHEDULE.find(d => d.date.isSame(schedulePickerState.date, 'day'))
        : undefined;
      const selectedSlot =
        selectedDay && schedulePickerState.slotIndex !== undefined
          ? selectedDay.slots[schedulePickerState.slotIndex]
          : undefined;
      if (!selectedSlot || !selectedSlot.enabled) return true;
    }

    return false;
  }, [arrival, departure, isNavigatingNext, partner, schedulePickerState.date, schedulePickerState.mode, schedulePickerState.slotIndex]);

  const buildPortLists = (
    portList: Port[],
    businessPartnerId: string,
    departurePortId?: string
  ): {
    departurePorts: Port[];
    arrivalPorts: Port[];
    resolvedDepartureId: string;
  } => {
    const departurePorts = portList.filter((p) => p.businessPartnerId === businessPartnerId);
    const resolvedDeparture = departurePortId
      ? departurePorts.find((p) => p.id === departurePortId) ?? departurePorts[0]
      : departurePorts[0];

    const resolvedDepartureId = resolvedDeparture?.id ?? '';
    const destinationIds = resolvedDeparture?.destinationPortList ?? [];
    const arrivalPorts = portList.filter(
      (p) => p.businessPartnerId === businessPartnerId && destinationIds.includes(p.id)
    );

    return { departurePorts, arrivalPorts, resolvedDepartureId };
  };

  //ポート情報を取得
  useEffect(() => {
    const fetchData = async () => {
      try {
        setDisplayLoadingMark(true);
        if (customerURLInformation == null) {
          navigate("/forbidden403Page");
          setDisplayLoadingMark(false);
          return;
        }
        let c: APIConnector = APIConnector.instance;
        let portList = await c.getPortListForCustomer(customerURLInformation!!.businessPartnerId, customerURLInformation!!.tenantId);
        //すべてのポートを設定
        setAllPortList(portList);
        const partnerIds = Array.from(new Set(portList.map((p) => p.businessPartnerId)));
        const resolvedPartnerId = partner || partnerIds[0] || '';

        if (!resolvedPartnerId) {
          setDeparturePortList([]);
          setArrivalPortList([]);
          setDisplayLoadingMark(false);
          return;
        }

        if (!partner) {
          setPartner(resolvedPartnerId);
        }

        const { departurePorts, arrivalPorts, resolvedDepartureId } = buildPortLists(
          portList,
          resolvedPartnerId,
          departure || undefined
        );

        setDeparturePortList(departurePorts);
        setArrivalPortList(arrivalPorts);

        if (!departure && resolvedDepartureId) {
          setDeparture(resolvedDepartureId);
        }

        if (arrival && !arrivalPorts.some((p) => p.id === arrival)) {
          setArrival('');
        }

        setDisplayLoadingMark(false);
      } catch (error) {
        setDisplayLoadingMark(false);
        console.error(error);
      }
    }
    fetchData();
    setSchedulePickerState(getDefaultSchedulePickerValue());
  }, []);

  const getDefaultSchedulePickerValue = () => {
    if (orderForEdit?.preferredDeliveryDate && orderForEdit?.preferredDeliveryTime) {
      let preferredDeliveryDateLocal = orderForEdit?.preferredDeliveryDate;
      let preferredDeliveryTimeLocal = orderForEdit?.preferredDeliveryTime;
      let searchSimpleTimeSlotResult = searchSimpleSchedule(
        dayjs(preferredDeliveryDateLocal),
        dayjs(preferredDeliveryDateLocal + " " + preferredDeliveryTimeLocal.split("-")[0]),
        dayjs(preferredDeliveryDateLocal + " " + preferredDeliveryTimeLocal.split("-")[1])
      );
      if (searchSimpleTimeSlotResult != null) {
        return {
          mode: 'SCHEDULED' as 'ASAP' | 'SCHEDULED',
          date: searchSimpleTimeSlotResult.slot.start,
          slotIndex: searchSimpleTimeSlotResult.index
        };
      }
    }
    return {
      mode: 'ASAP' as 'ASAP' | 'SCHEDULED',
      date: MEDICAL_STUB_SIMPLE_SCHEDULE[0]?.date,
      slotIndex: MEDICAL_STUB_SIMPLE_SCHEDULE[0]?.slots.findIndex(s => s.enabled)
    };
  };

  /**
   * 出発地が変更されたときのハンドラ
   * @param event 
   */
  const handleChangeDeparture = (event: any) => {
    const newDepartureId = event.target.value;
    setDeparture(newDepartureId);
    //出発地が変更された場合、到着地をクリアする
    setArrival("");

    const { arrivalPorts } = buildPortLists(allPortList, partner, newDepartureId);
    setArrivalPortList(arrivalPorts);
  };

  /**
   * 到着地が変わった時のハンドラ
   * @param event 
   */
  const handleChangeArrival = (event: any) => {
    setArrival(event.target.value);
  };

  /**
   * 次のページに遷移する際の処理
   * @param event 
   */
  const handleToNextPage = async () => {
    if (isNextDisabled) return;

    try {
      setIsNavigatingNext(true);

      const departurePort = allPortList.find((val) => departure === val.id) as Port;
      const arrivalPort = allPortList.find((val) => arrival === val.id) as Port;

      const chosenDate = schedulePickerState.date;
      const chosenSlot =
        schedulePickerState.slotIndex !== undefined
          ? MEDICAL_STUB_SIMPLE_SCHEDULE.find(d => d.date.isSame(chosenDate, 'day'))?.slots[schedulePickerState.slotIndex]
          : undefined;

      const updatedOrder: MaritimeOrderInfo = {
        ...order,
        departure: departurePort,
        arrival: arrivalPort,
        businessPartnerId: partner,
        preferredDeliveryDate: chosenDate?.format('YYYY-MM-DD'),
        preferredDeliveryTime: chosenSlot ? `${chosenSlot.start.format('HH:mm')}-${chosenSlot.end.format('HH:mm')}` : "指定なし（最速で配送）",
      };

      setOrder(updatedOrder);

      navigate(
        "/ordergoodsanddestinationinput/" + customerURLInformation?.urlId,
        {
          state: {
            order: updatedOrder
          },
        }
      );
    } catch (error) {
      console.error(error);
    } finally {
      setIsNavigatingNext(false);
    }
  };

  return (
    <div className="App">
      <Grid container justifyContent="center" item xs={12} spacing={1}>
        <Backdrop className={classes.backdrop} open={isDisplayLoadingMark}>
          <CircularProgress color="inherit" />
        </Backdrop>
        <Grid item xs={12} sm={12} >
          <ApplicationTooBar label="配送依頼" />
        </Grid>
        <Grid item xs={12} sm={12} >
          <OrderStepBar process={Process.FlightSelect} />
        </Grid>
        <SectionGrid title="配送区間指定" icon={<LocationOnIcon />}>
          <Grid container spacing={1} justifyContent="center" alignItems="center">
            <Grid item xs={12} sm="auto" sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
              <FormControl className={classes.formControl} sx={{ m: 0, minWidth: 220 }}>
                <InputLabel id="dep-label">出発</InputLabel>
                <Select
                  variant="outlined"
                  className={classes.select}
                  labelId="dep-label"
                  id="dep"
                  value={departure}
                  onChange={handleChangeDeparture}
                  label="出発">
                  {Array.from(departurePortList.values()).map((port) => (
                    <MenuItem key={port.id} value={port.id}>{port.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm="auto" sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
              <FormControl className={classes.formControl} sx={{ m: 0, minWidth: 220 }}>
                <InputLabel id="arv-label">到着</InputLabel>
                <Select
                  variant="outlined"
                  className={classes.select}
                  labelId="arv-label"
                  id="arv"
                  value={arrival}
                  onChange={handleChangeArrival}
                  label="到着">
                  {Array.from(arrivalPortList.values()).map((port) =>
                    <MenuItem key={port.id} value={port.id}>{port.name}</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </SectionGrid>
        <SectionGrid title="フライト希望日時" icon={<ScheduleIcon />}>
          <DeliveryTimePicker
            schedule={MEDICAL_STUB_SIMPLE_SCHEDULE}
            mode={schedulePickerState.mode}
            selectedDate={schedulePickerState.date}
            selectedSlotIndex={schedulePickerState.slotIndex}
            onChange={(v) => setSchedulePickerState({ mode: v.mode, date: v.date ?? dayjs().startOf('day'), slotIndex: v.slotIndex ?? MEDICAL_STUB_SIMPLE_SCHEDULE[0].slots.findIndex(s => s.enabled) })}
          />
        </SectionGrid>
        <OrderBackNextButtons
          hideBack
          onNext={() => handleToNextPage()}
          nextDisabled={isNextDisabled}
        />
      </Grid>
    </div>
  );
}